import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { ClockCounterClockwise, FloppyDisk, ArrowCounterClockwise, Eye, Image, TextAa, Lightning, ArrowsClockwise, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionIcons = {
  'created': FloppyDisk,
  'script_processed': TextAa,
  'batch_generated': Lightning,
  'batch_regenerated': ArrowsClockwise,
  'manual_save': FloppyDisk,
  'settings_updated': FloppyDisk,
  'restored': ArrowCounterClockwise,
  'pre_restore': ClockCounterClockwise,
};

const actionColors = {
  'created': '#10B981',
  'script_processed': '#3B82F6',
  'batch_generated': '#FF5D01',
  'batch_regenerated': '#8B5CF6',
  'manual_save': '#F59E0B',
  'settings_updated': '#6B7280',
  'restored': '#10B981',
  'pre_restore': '#6B7280',
};

export const VersionHistoryModal = ({ open, onClose, projectId, onRestored }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && projectId) {
      fetchVersions();
    }
  }, [open, projectId]);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/versions`);
      setVersions(res.data.versions || []);
    } catch (err) {
      console.error('Failed to fetch versions:', err);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVersion = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/versions/save?description=Manual%20save`);
      toast.success(`Saved version ${res.data.version_number}`);
      fetchVersions();
    } catch (err) {
      toast.error('Failed to save version');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (version) => {
    const confirmed = window.confirm(
      `Restore to version ${version.version_number}?\n\nThis will replace your current project state with the snapshot from "${version.description}".\n\nA backup of your current state will be created automatically.`
    );
    if (!confirmed) return;

    setRestoring(version.version_id);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/versions/${version.version_id}/restore`);
      toast.success(res.data.message);
      if (onRestored) {
        onRestored();
      }
      onClose();
    } catch (err) {
      toast.error('Failed to restore version');
    } finally {
      setRestoring(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#171717] border-[#333333] text-white max-w-lg max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <ClockCounterClockwise size={24} weight="fill" className="text-[#FF5D01]" />
            Version History
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {/* Save button */}
          <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#333333]">
            <span className="text-sm text-[#A1A1AA]">
              {versions.length} version{versions.length !== 1 ? 's' : ''} saved
            </span>
            <Button
              size="sm"
              onClick={handleSaveVersion}
              disabled={saving}
              className="bg-[#FF5D01] hover:bg-[#e65400] text-white gap-2"
              data-testid="save-version-btn"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FloppyDisk size={16} weight="fill" />
              )}
              Save Current
            </Button>
          </div>

          {/* Version list */}
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-[#FF5D01] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-[#A1A1AA]">Loading versions...</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <ClockCounterClockwise size={40} className="mx-auto mb-3 text-[#A1A1AA] opacity-50" />
              <p className="text-[#A1A1AA]">No versions yet</p>
              <p className="text-xs text-[#666] mt-1">Versions are created when you process scripts or generate images</p>
            </div>
          ) : (
            <ScrollArea className="h-[350px]">
              <div className="space-y-2 pr-3">
                {versions.map((version, index) => {
                  const Icon = actionIcons[version.action] || FloppyDisk;
                  const color = actionColors[version.action] || '#A1A1AA';
                  const isLatest = index === 0;
                  const isRestoring = restoring === version.version_id;

                  return (
                    <div
                      key={version.version_id}
                      className={`p-3 rounded-lg border transition-colors ${
                        isLatest 
                          ? 'bg-[#FF5D01]/5 border-[#FF5D01]/30' 
                          : 'bg-[#0a0a0a] border-[#333333] hover:border-[#444]'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${color}20` }}
                        >
                          <Icon size={16} weight="fill" style={{ color }} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">v{version.version_number}</span>
                            {isLatest && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-[#FF5D01] text-white rounded">
                                CURRENT
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#A1A1AA] mb-1">{version.description}</p>
                          <div className="flex items-center gap-3 text-xs text-[#666]">
                            <span>{formatRelativeTime(version.created_at)}</span>
                            <span className="flex items-center gap-1">
                              <TextAa size={12} />
                              {version.scene_count} scenes
                            </span>
                            <span className="flex items-center gap-1">
                              <Image size={12} />
                              {version.images_count || 0} images
                            </span>
                          </div>
                        </div>

                        {/* Restore button */}
                        {!isLatest && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestore(version)}
                            disabled={!!restoring}
                            className="text-[#A1A1AA] hover:text-white hover:bg-[#262626] h-8 px-2"
                            data-testid={`restore-version-${version.version_number}-btn`}
                          >
                            {isRestoring ? (
                              <div className="w-4 h-4 border-2 border-[#A1A1AA] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <ArrowCounterClockwise size={16} />
                            )}
                          </Button>
                        )}
                        {isLatest && (
                          <CheckCircle size={20} weight="fill" className="text-[#10B981]" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
